import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { getFilteredObject } from '../../../services/filterObject';
import { getAsLongDate, getAsStartAndEndTimeFormat } from '../../../common-client2/src/services/ukgDateTime';
import SubmittableSearchBar from '../../../common-client2/src/components/SubmittableSearchBar';
import { dynamicSort } from '../../../common-client2/src/services/sort';
import { buildSegmentInfo } from '../../../EmploymentBillboard/App/EmployeeDashboard/Utils/uiGenerators';
import { Table, Checkbox } from 'rsuite';
import { Paper } from '@material-ui/core';

const { Column, HeaderCell, Cell } = Table;

const rowKey = 'id';
/** @typedef {'asc'|'desc'} SortType */
/** @type {SortType} */
const defaultSortType = 'asc';

/** @param {ApiManager.Segments[]} segments */
const getFirstSegmentLocation = ([{ shortOrgJobPath }]) => shortOrgJobPath || '';

/** @param {ApiManager.Segments[]} segments */
const getBatchOfSegments = (segments) => (<>{segments.map((segment, index) => buildSegmentInfo(segment, index))}</>);

/**
 * @param {ApiManager.OpenShiftsByWorkRulePresentationModel[]} openShiftOptions 
 * @returns {ApiManager.IOpenShiftTableEntry[]}
 */
const mapToOpenShiftTableEntry = (openShiftOptions) => openShiftOptions.map(x => ({
    ...x,
    startEndDateTimeSort: `${new Date(x.startDateTime).getTime()} ${new Date(x.endDateTime).getTime()}`,
    location: getFirstSegmentLocation(x.segments),
    longDateForSearch: getAsLongDate(x.startDateTime),
    startAndEndTimeForSearch: getAsStartAndEndTimeFormat(x.startDateTime, x.endDateTime),
}))

/**
 * @param {{
 *  onSelect: () => void;
 *  onCheckAll: (checked: boolean, value: number[]) => void;
 *  selectedOptions: number[];
 *  openShiftOptions: ApiManager.OpenShiftsByWorkRulePresentationModel[];
 * }} param0 
 */
const OpenShiftPickerTable = ({
    onSelect,
    onCheckAll,
    selectedOptions,
    openShiftOptions,
}) => {
    const { t } = useTranslation('common', { keyPrefix: 'openShiftPicker.table' });
    const [sortValues, setSortValues] = useState({ sortColumn: 'startEndDateTimeSort', sortType: defaultSortType });
    const [tableEntries] = useState(mapToOpenShiftTableEntry(openShiftOptions));
    const [filteredData, setFilteredData] = useState(tableEntries);
    let isSelectAllChecked = false;
    let isSelectAllIndeterminate = false;

    if (filteredData.every(x => selectedOptions.includes(x.id))) {
        isSelectAllChecked = true;
    } else if (filteredData.every(x => !selectedOptions.includes(x.id))) {
        isSelectAllChecked = false;
    } else if (selectedOptions.length > 0) {
        isSelectAllIndeterminate = true;
    }
    const ref = useRef(null);

    useEffect(() => {
        const sortKey = sortValues.sortType === 'desc' ? `-${sortValues.sortColumn}` : sortValues.sortColumn;
        var sortedData = _.cloneDeep(filteredData?.sort(dynamicSort(sortKey)));
        setFilteredData(sortedData);
    }, [sortValues.sortType, sortValues.sortColumn])

    return (
        <div className="OpenShiftPickerTable">
            <SubmittableSearchBar
                isTriggerSearchOnChange
                onChange={(searchValue) => setFilteredData(getFilteredObject(tableEntries, searchValue))}
            />
            <Paper
                elevation={2}
                className="open-shift-picker-table-container"
                ref={ref}
            >
                <Table
                    sortColumn={sortValues.sortColumn}
                    sortType={sortValues.sortType}
                    onSortColumn={(sortColumn, sortType) => setSortValues({ sortColumn, sortType })}
                    autoHeight
                    data={filteredData}
                    id="selectable-open-shifts-table"
                    className="table-section-open-shift"
                    rowKey={rowKey}
                    shouldUpdateScroll={false}
                    wordWrap
                    locale={{ emptyMessage: t('noOpenShiftFound') }}
                >
                    <Column
                        width={50}
                        fixed
                        align="center"
                        key="id"
                    >
                        <HeaderCell>
                            <Checkbox
                                inline
                                checked={isSelectAllChecked}
                                indeterminate={isSelectAllIndeterminate}
                                onChange={(_, checked) => onCheckAll(checked, filteredData.map(x => x.id))}
                            />
                        </HeaderCell>
                        <Cell>
                            {/** @param {ApiManager.OpenShiftsByWorkRulePresentationModel} param0 */
                                ({ id }) => (
                                    <Checkbox
                                        value={id}
                                        inline
                                        onChange={onSelect}
                                        checked={selectedOptions.some((item) => item === id)}
                                    />
                                )
                            }
                        </Cell>
                    </Column>
                    <Column
                        flexGrow={2}
                        sortable
                        key="startEndDateTimeSort"
                    >
                        <HeaderCell>{t('headers.shift')}</HeaderCell>
                        <Cell dataKey="startEndDateTimeSort">
                            {
                                (/** @type {ApiManager.OpenShiftsByWorkRulePresentationModel} */ rowData) => (
                                    <>
                                        <span className="start-date text-md-semibold">
                                            {getAsLongDate(rowData.startDateTime)}
                                        </span>
                                        <span className="start-time-end-time">
                                            {getAsStartAndEndTimeFormat(rowData.startDateTime, rowData.endDateTime)}
                                        </span>
                                    </>

                                )
                            }
                        </Cell>
                    </Column>
                    <Column
                        flexGrow={4}
                        sortable
                        key="location"
                    >
                        <HeaderCell>{t('headers.locations')}</HeaderCell>
                        <Cell dataKey="location">
                            {
                                (/** @type {ApiManager.OpenShiftsByWorkRulePresentationModel} */ rowData) => (
                                    getFirstSegmentLocation(rowData.segments)
                                )
                            }
                        </Cell>
                    </Column>
                    <Column
                        flexGrow={4}
                        key="segments"
                    >
                        <HeaderCell>{t('headers.segments')}</HeaderCell>
                        <Cell dataKey="segments">
                            {
                                (/** @type {ApiManager.OpenShiftsByWorkRulePresentationModel} */ rowData) => (
                                    getBatchOfSegments(rowData.segments)
                                )
                            }
                        </Cell>
                    </Column>
                </Table>
            </Paper>
        </div>
    );
};

export default OpenShiftPickerTable;
